import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { RewriteAfterRenderer } from './RewriteAfterRenderer'

type RewriteDrawerProps = {
  isOpen: boolean
  onClose: () => void
  selectedText: string
  instruction: string
  onInstructionChange: (value: string) => void
  onRewrite: () => Promise<void>
  isRewritePending: boolean
  result: string
  hasResult: boolean
  onAccept: () => void
  onReject: () => void
  /** When set, the drawer shows this message and only allows closing. */
  blockedMessage?: string | null
}

export function RewriteDrawer({
  isOpen,
  onClose,
  selectedText,
  instruction,
  onInstructionChange,
  onRewrite,
  isRewritePending,
  result,
  hasResult,
  onAccept,
  onReject,
  blockedMessage = null,
}: RewriteDrawerProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isBlocked = (blockedMessage?.length ?? 0) > 0
  const hasInstruction = instruction.trim().length > 0

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent side='right' className='flex flex-col gap-0 p-0 sm:max-w-sm'>
        <SheetHeader className='shrink-0 border-b px-4 py-3'>
          <SheetTitle className='text-lg'>
            {t('riskAssessmentPage.rewriteDrawer.title')}
          </SheetTitle>
        </SheetHeader>
        {isBlocked ? (
          <div className='flex flex-1 flex-col gap-4 p-4'>
            <Alert>
              <AlertDescription>{blockedMessage}</AlertDescription>
            </Alert>
            <Button type='button' onClick={onClose} className='mt-auto'>
              {t('riskAssessmentPage.rewriteDrawer.close')}
            </Button>
          </div>
        ) : (
          <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-4'>
            <div>
              <Label className='mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                {t('riskAssessmentPage.rewriteDrawer.selectedTextLabel')}
              </Label>
              <pre className='mt-1 max-h-32 overflow-y-auto rounded-md border border-input bg-muted/50 p-3 text-sm font-mono text-foreground whitespace-pre-wrap'>
                {selectedText ||
                  t('riskAssessmentPage.rewriteDrawer.noSelectionFallback')}
              </pre>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='rewrite-instruction'>
                {t('riskAssessmentPage.rewriteDrawer.instructionLabel')}
              </Label>
              <Textarea
                id='rewrite-instruction'
                rows={4}
                value={instruction}
                onChange={(evt) => onInstructionChange(evt.target.value)}
                className='resize-none'
              />
            </div>
            <Button
              onClick={() => void onRewrite()}
              disabled={isRewritePending || !hasInstruction}
              className='gap-2'
            >
              {isRewritePending ? (
                <>
                  <Loader2 className='size-4 animate-spin' />
                  {t('riskAssessmentPage.rewriteDrawer.rewriteButton')}
                </>
              ) : (
                t('riskAssessmentPage.rewriteDrawer.rewriteButton')
              )}
            </Button>
            {hasResult && (
              <>
                <RewriteAfterRenderer result={result} />
                <div className='flex gap-2'>
                  <Button onClick={onAccept}>
                    {t('riskAssessmentPage.rewriteDrawer.accept')}
                  </Button>
                  <Button onClick={onReject} variant='outline'>
                    {t('riskAssessmentPage.rewriteDrawer.reject')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
